import { useQuery } from '@tanstack/react-query';
import type { Ref } from 'react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/Button';
import { Dropdown } from '~/components/Dropdown';
import { IconButton } from '~/components/IconButton';
import { Label } from '~/components/Label/Label';
import { Separator } from '~/components/Separator';
import { cn } from '~/lib/cn';
import { mapLabelHierarchyToCheckboxTreeItem } from '~/modules/artist-roster/components/ArtistFilter/utils';
import { CheckboxTree } from '~/modules/artist-roster/components/CheckboxTree';
import type { CheckboxTreeItem } from '~/modules/artist-roster/components/CheckboxTree/types';
import { flattenCheckboxTree } from '~/modules/artist-roster/components/CheckboxTree/utils';
import { getLabelHierarchy } from '~/modules/artist-roster/lib/labels';

import { ArtistCrossoverFilter } from './ArtistCrossoverFilter/ArtistCrossoverFilter';
import { ArtistOnRosterFilter } from './ArtistOnRosterFilter/ArtistOnRosterFilter';
import { DEFAULT_ARTIST_FILTERS } from './constants';
import type { ArtistFilterProps, ArtistFiltersType } from './types';

const PANEL_CLASSES = {
  hidden: 'translate-x-[400px]',
  visible: 'translate-x-0',
};

export type FiltersPanelRef = {
  closePanel: () => void;
};

export const ArtistFilterPanel = forwardRef(
  (
    { originalFilters, onClose, onApplyFilters }: ArtistFilterProps,
    ref: Ref<FiltersPanelRef>,
  ) => {
    const { t } = useTranslation('roster');
    const panelRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);

    const closePanel = useCallback(() => {
      panelRef.current?.classList.add(PANEL_CLASSES.hidden);
      panelRef.current?.classList.remove(PANEL_CLASSES.visible);
    }, []);

    useImperativeHandle(ref, () => ({
      closePanel,
    }));

    const [labelTreeItems, setLabelTreeItems] = useState<CheckboxTreeItem[]>(
      [],
    );
    const [filterSelections, setFilterSelections] =
      useState<ArtistFiltersType>(originalFilters);

    const { data } = useQuery({
      queryKey: ['labels-hierarchies'],
      queryFn: () => getLabelHierarchy(),
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    });

    useEffect(() => {
      if (data?.items) {
        const originalLabelIds = new Set(
          originalFilters.labels.map((label) => label.labelId),
        );
        setLabelTreeItems(
          mapLabelHierarchyToCheckboxTreeItem(data.items, originalLabelIds),
        );
      }
    }, [data, originalFilters.labels]);

    useEffect(() => {
      setFilterSelections((prevFilters) => {
        return {
          ...prevFilters,
          labels: flattenCheckboxTree(labelTreeItems)
            .filter((label) => label.checked)
            .map((label) => ({
              labelId: parseInt(label.id),
              name: label.label,
            })),
        };
      });
    }, [labelTreeItems]);

    /**
     * @TODO WMG1-426
     * This logic to manage state is repetitious and not scaling well.
     * Investigate whether react-hook-form can be used instead to manage state.
     */
    const isDirty = useMemo(() => {
      const isCrossoverDirty =
        filterSelections.isCrossover !== originalFilters.isCrossover;
      const isLabelDirty =
        filterSelections.labels.length !== originalFilters.labels.length;
      const isOnRosterDirty =
        filterSelections.isOnRoster !== originalFilters.isOnRoster;

      let labelIdsAreEqual = true;
      for (const label of filterSelections.labels) {
        if (!filterSelections.labels.find((l) => l.labelId === label.labelId)) {
          labelIdsAreEqual = false;
          break;
        }
      }

      return (
        isLabelDirty || !labelIdsAreEqual || isCrossoverDirty || isOnRosterDirty
      );
    }, [
      filterSelections.isCrossover,
      filterSelections.isOnRoster,
      filterSelections.labels,
      originalFilters.isCrossover,
      originalFilters.isOnRoster,
      originalFilters.labels.length,
    ]);

    const handleApplyFilters = useCallback(() => {
      onApplyFilters(filterSelections);
      onClose();
    }, [filterSelections, onApplyFilters, onClose]);

    const handleCrossoverChanged = useCallback((isCrossover: boolean) => {
      setFilterSelections((prevFilters) => {
        return { ...prevFilters, isCrossover };
      });
    }, []);

    const handleOnRosterChanged = useCallback((isOnRoster?: boolean) => {
      setFilterSelections((prevFilters) => {
        return { ...prevFilters, isOnRoster };
      });
    }, []);

    const handleLabelsTreeChange = useCallback(
      (newItems: CheckboxTreeItem[]) => {
        setLabelTreeItems(newItems);
      },
      [],
    );

    const handleClearFilters = useCallback(() => {
      // Uncheck all the labels in the tree.
      setLabelTreeItems(mapLabelHierarchyToCheckboxTreeItem(data.items));
      setFilterSelections(DEFAULT_ARTIST_FILTERS);
    }, [data?.items]);

    // Focus inside the Filters panel after it opens.
    useEffect(() => {
      closeButtonRef.current?.focus();
    }, [closeButtonRef]);

    // Animate the panel into view after it mounts.
    useEffect(() => {
      panelRef.current?.classList.add(PANEL_CLASSES.visible);
      panelRef.current?.classList.remove(PANEL_CLASSES.hidden);
    }, [panelRef]);

    return (
      <div
        ref={panelRef}
        className={cn(
          'fixed inset-y-0 right-0 w-[400px] border-l duration-300 ease-out bg-level-02 border-l-emphasis-subtle',
          PANEL_CLASSES.hidden,
        )}
      >
        <div className="flex flex-col gap-lg p-lg">
          <div className="flex justify-between">
            <div className="text-headline-md font-bold">
              {t('filters.heading')}
            </div>
            <IconButton
              ref={closeButtonRef}
              iconType="xmark"
              iconVariant="regular"
              size="large"
              onClick={onClose}
              className="duration-300 ease-out"
              data-testid="close-btn"
            />
          </div>
          <section className="flex flex-col gap-sm">
            <Label className="font-semibold">{t('filters.label')}</Label>
            <Dropdown
              options={[]}
              placeholder={
                filterSelections.labels.length > 0
                  ? t('filters.labelsWithCount', {
                      count: filterSelections.labels.length,
                    })
                  : t('filters.selectLabels')
              }
              contentClassName="z-10"
            >
              <CheckboxTree
                value={labelTreeItems}
                setValue={setLabelTreeItems}
                onChange={handleLabelsTreeChange}
              />
            </Dropdown>
          </section>
          <Separator />
          <section>
            <Label className="font-semibold">{t('filters.artistType')}</Label>
            <ArtistCrossoverFilter
              isCrossoverFilterSelected={filterSelections.isCrossover}
              onSelect={handleCrossoverChanged}
            />
          </section>
          <Separator />
          <section>
            <Label className="font-semibold">{t('filters.status')}</Label>
            <ArtistOnRosterFilter
              isOnRoster={filterSelections.isOnRoster}
              onSelect={handleOnRosterChanged}
            />
          </section>
          <Separator />
        </div>

        <div className="absolute bottom-0 flex w-full justify-end gap-lg border-t-px px-lg py-md border-emphasis-subtle">
          <Button
            size="large"
            variant="tertiary"
            className="disabled:cursor-not-allowed"
            disabled={
              filterSelections.labels.length === 0 &&
              !filterSelections.isCrossover &&
              filterSelections.isOnRoster === undefined
            }
            onClick={handleClearFilters}
          >
            {t('filters.clearFilters')}
          </Button>
          <Button
            size="large"
            className="disabled:cursor-not-allowed"
            disabled={!isDirty}
            onClick={handleApplyFilters}
          >
            {t('filters.apply')}
          </Button>
        </div>
      </div>
    );
  },
);

ArtistFilterPanel.displayName = 'ArtistFilterPanel';
